/**
 * LP Click Rate Page
 *
 * Shows the rate at which LP visitors click the CTA to start intake,
 * per landing page path. Click into a page to see source/campaign breakdown.
 */

import { format } from 'date-fns'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { trafficAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, ArrowLeft, MousePointer
} from 'lucide-react'
import { DateRangePicker } from '../components/DateRangePicker'

function formatNum(v) {
  if (v == null || isNaN(v)) return '-'
  return new Intl.NumberFormat('en-US').format(v)
}

function formatPct(v) {
  if (v == null || isNaN(v)) return '-'
  return v.toFixed(2) + '%'
}

function rateColor(rate) {
  if (rate == null) return ''
  if (rate >= 10) return 'text-green-600 font-semibold'
  if (rate >= 5) return 'text-blue-600 font-semibold'
  if (rate >= 1) return 'text-foreground'
  return 'text-red-500 font-semibold'
}

export default function LpClickRatePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPath, setSelectedPath] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [groupBy, setGroupBy] = useState('source_medium_campaign')

  const now = new Date()
  const defaultFrom = new Date(now); defaultFrom.setDate(now.getDate() - 7)

  const [dateRange, setDateRange] = useState(() => {
    const s = searchParams.get('start')
    const e = searchParams.get('end')
    if (s && e) return { from: new Date(s + 'T12:00:00'), to: new Date(e + 'T12:00:00') }
    return { from: defaultFrom, to: now }
  })

  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setSelectedPath(null)
      setDetail(null)
      const res = await trafficAPI.getLpClickRate({ start: startDate, end: endDate })
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    setSearchParams({ start: startDate, end: endDate }, { replace: true })
  }, [startDate, endDate, setSearchParams])

  const loadDetail = useCallback(async (path, gb = groupBy) => {
    setSelectedPath(path)
    setDetail(null)
    try {
      setDetailLoading(true)
      const res = await trafficAPI.getLpClickRateDetail({ start: startDate, end: endDate, path, groupBy: gb })
      if (res.data.success) setDetail(res.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setDetailLoading(false)
    }
  }, [startDate, endDate, groupBy])

  useEffect(() => {
    if (selectedPath) loadDetail(selectedPath, groupBy)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy])

  const summary = data?.summary
  const pages = data?.pages || []

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">LP Click Rate</h1>
          <p className="text-sm text-muted-foreground mt-1">
            CTA click-through rate per landing page (visit-intake events vs page views)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={(r) => { if (r?.from && r?.to) setDateRange(r) }} />
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive"><CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-5 h-5" /><span>{error}</span>
          </div>
        </CardContent></Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && !selectedPath && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">LP Visitors</span>
              <p className="text-xl font-bold">{formatNum(summary?.total_visitors)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">CTA Clickers</span>
              <p className="text-xl font-bold">{formatNum(summary?.total_clickers)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Overall Click Rate</span>
              <p className={`text-xl font-bold ${rateColor(summary?.overall_click_rate)}`}>{formatPct(summary?.overall_click_rate)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Pages Tracked</span>
              <p className="text-xl font-bold">{formatNum(summary?.total_pages)}</p>
            </CardContent></Card>
          </div>

          {/* Pages table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Landing Pages</CardTitle>
            </CardHeader>
            <CardContent>
              {pages.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Page Path</th>
                        <th className="text-right p-3 font-medium">Visitors</th>
                        <th className="text-right p-3 font-medium">CTA Clicks</th>
                        <th className="text-right p-3 font-medium">Click Rate</th>
                        <th className="text-right p-3 font-medium">
                          <div className="w-32 text-right">Visual</div>
                        </th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pages.map(p => (
                        <tr key={p.page_path} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => loadDetail(p.page_path)}>
                          <td className="p-3 font-mono text-sm font-medium">{p.page_path}</td>
                          <td className="p-3 text-right font-mono">{formatNum(p.visitors)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(p.clickers)}</td>
                          <td className={`p-3 text-right font-mono ${rateColor(p.click_rate)}`}>{formatPct(p.click_rate)}</td>
                          <td className="p-3">
                            <div className="w-32 h-3 bg-muted rounded-full overflow-hidden ml-auto">
                              <div
                                className={`h-full rounded-full ${p.click_rate >= 5 ? 'bg-green-500' : p.click_rate >= 1 ? 'bg-blue-500' : 'bg-red-400'}`}
                                style={{ width: `${Math.min(p.click_rate * 2, 100)}%` }}
                              />
                            </div>
                          </td>
                          <td className="p-3">
                            <MousePointer className="h-4 w-4 text-muted-foreground" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Detail drilldown */}
      {selectedPath && (
        <>
          <Button variant="ghost" size="sm" onClick={() => { setSelectedPath(null); setDetail(null) }} className="gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to pages
          </Button>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="text-base">
                  {selectedPath}
                  {detailLoading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
                </CardTitle>
                <div className="inline-flex rounded-md border bg-muted/30 p-0.5 text-xs">
                  {[
                    { v: 'source_medium_campaign', label: 'Source × Medium × Campaign' },
                    { v: 'campaign', label: 'Campaign' },
                    { v: 'medium', label: 'Medium' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setGroupBy(opt.v)}
                      className={`px-2.5 py-1 rounded-sm transition ${
                        groupBy === opt.v ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {detailLoading && !detail ? (
                <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
              ) : detail?.breakdown?.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No breakdown data</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        {groupBy === 'source_medium_campaign' && (
                          <>
                            <th className="text-left p-3 font-medium">Source</th>
                            <th className="text-left p-3 font-medium">Medium</th>
                            <th className="text-left p-3 font-medium">Campaign</th>
                          </>
                        )}
                        {groupBy === 'campaign' && <th className="text-left p-3 font-medium">Campaign</th>}
                        {groupBy === 'medium' && <th className="text-left p-3 font-medium">Medium</th>}
                        <th className="text-right p-3 font-medium">Visitors</th>
                        <th className="text-right p-3 font-medium">Clickers</th>
                        <th className="text-right p-3 font-medium">Click Rate</th>
                        <th className="text-right p-3 font-medium">
                          <div className="w-24 text-right">Visual</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail?.breakdown || []).map((r, i) => (
                        <tr key={i} className="border-t hover:bg-muted/30">
                          {groupBy === 'source_medium_campaign' && (
                            <>
                              <td className="p-3 text-sm">{r.utm_source}</td>
                              <td className="p-3 text-xs text-muted-foreground">{r.utm_medium}</td>
                              <td className="p-3 text-xs break-words max-w-[250px]">{r.utm_campaign}</td>
                            </>
                          )}
                          {groupBy === 'campaign' && (
                            <td className="p-3 text-xs break-words max-w-[400px]">{r.utm_campaign}</td>
                          )}
                          {groupBy === 'medium' && (
                            <td className="p-3 text-sm">{r.utm_medium}</td>
                          )}
                          <td className="p-3 text-right font-mono">{formatNum(r.visitors)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.clickers)}</td>
                          <td className={`p-3 text-right font-mono ${rateColor(r.click_rate)}`}>{formatPct(r.click_rate)}</td>
                          <td className="p-3">
                            <div className="w-24 h-3 bg-muted rounded-full overflow-hidden ml-auto">
                              <div
                                className={`h-full rounded-full ${r.click_rate >= 5 ? 'bg-green-500' : r.click_rate >= 1 ? 'bg-blue-500' : 'bg-red-400'}`}
                                style={{ width: `${Math.min(r.click_rate * 2, 100)}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
